import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  getConversationThreadClass,
  getMessageBubbleClasses,
} from "../../utils/messages/conversationThemes";

function buildApiUrl(apiBase, path) {
  const p = String(path || "");
  if (/^https?:\/\//i.test(p)) return p;
  const base = String(apiBase || "");
  return `${base}${p.startsWith("/") ? p : `/${p}`}`;
}

export default function useConversationSettings({
  apiBase,
  isGroupChat,
  selectedFriendId,
  selectedGroupId,
  peerUsername,
}) {
  const [settings, setSettings] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const apiUrl = useCallback((path) => buildApiUrl(apiBase, path), [apiBase]);

  const themeKey = settings?.themeKey || "default";

  const threadClass = useMemo(() => getConversationThreadClass(themeKey), [themeKey]);

  const bubbleClasses = useCallback(
    (isOwn) => getMessageBubbleClasses(themeKey, isOwn),
    [themeKey]
  );

  const displayTitleDm = useMemo(() => {
    const nick = settings?.myNicknameForPeer;
    if (nick && String(nick).trim()) return String(nick).trim();
    return peerUsername || "";
  }, [settings?.myNicknameForPeer, peerUsername]);

  const resolveGroupMemberLabel = useCallback(
    (userId, username) => {
      const map = settings?.nicknamesByUserId || {};
      const id = Number(userId);
      const nick = map[id] ?? map[userId];
      if (nick && String(nick).trim()) return String(nick).trim();
      return username || "";
    },
    [settings?.nicknamesByUserId]
  );

  useEffect(() => {
    let cancelled = false;

    if (isGroupChat && !selectedGroupId) {
      setSettings(null);
      setMediaItems([]);
      setError("");
      return undefined;
    }
    if (!isGroupChat && !selectedFriendId) {
      setSettings(null);
      setMediaItems([]);
      setError("");
      return undefined;
    }

    (async () => {
      setLoading(true);
      setError("");
      try {
        if (isGroupChat) {
          const [sRes, mRes] = await Promise.all([
            axios.get(apiUrl(`/api/conversation-settings/group/${selectedGroupId}`), {
              withCredentials: true,
            }),
            axios.get(apiUrl(`/api/conversation-settings/group/${selectedGroupId}/media`), {
              withCredentials: true,
            }),
          ]);
          if (!cancelled) {
            setSettings(sRes.data);
            setMediaItems(mRes.data?.items || []);
          }
        } else {
          const [sRes, mRes] = await Promise.all([
            axios.get(apiUrl(`/api/conversation-settings/dm/${selectedFriendId}`), {
              withCredentials: true,
            }),
            axios.get(apiUrl(`/api/conversation-settings/dm/${selectedFriendId}/media`), {
              withCredentials: true,
            }),
          ]);
          if (!cancelled) {
            setSettings(sRes.data);
            setMediaItems(mRes.data?.items || []);
          }
        }
      } catch (err) {
        console.error("[conversation-settings]", err);
        if (!cancelled) {
          setError("Could not load conversation settings");
          setSettings(
            isGroupChat
              ? { scope: "group", themeKey: "default", nicknamesByUserId: {} }
              : { scope: "dm", themeKey: "default", myNicknameForPeer: null }
          );
          setMediaItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiUrl, isGroupChat, selectedFriendId, selectedGroupId]);

  const updateTheme = useCallback(
    async (nextKey) => {
      if (isGroupChat && selectedGroupId) {
        const res = await axios.patch(
          apiUrl(`/api/conversation-settings/group/${selectedGroupId}/theme`),
          { themeKey: nextKey },
          { withCredentials: true }
        );
        setSettings((prev) => ({ ...prev, themeKey: res.data.themeKey }));
      } else if (!isGroupChat && selectedFriendId) {
        const res = await axios.patch(
          apiUrl(`/api/conversation-settings/dm/${selectedFriendId}/theme`),
          { themeKey: nextKey },
          { withCredentials: true }
        );
        setSettings((prev) => ({ ...prev, themeKey: res.data.themeKey }));
      }
    },
    [apiUrl, isGroupChat, selectedFriendId, selectedGroupId]
  );

  const saveDmNickname = useCallback(
    async (nickname) => {
      if (!selectedFriendId) return;
      const res = await axios.put(
        apiUrl(`/api/conversation-settings/dm/${selectedFriendId}/nickname`),
        { nickname },
        { withCredentials: true }
      );
      setSettings((prev) => ({
        ...prev,
        myNicknameForPeer: res.data.myNicknameForPeer,
      }));
    },
    [apiUrl, selectedFriendId]
  );

  const saveGroupNickname = useCallback(
    async (targetUserId, nickname) => {
      if (!selectedGroupId) return;
      const res = await axios.put(
        apiUrl(`/api/conversation-settings/group/${selectedGroupId}/nickname`),
        { targetUserId, nickname },
        { withCredentials: true }
      );
      const tid = Number(res.data.targetUserId);
      setSettings((prev) => {
        const next = { ...prev, nicknamesByUserId: { ...(prev.nicknamesByUserId || {}) } };
        if (res.data.nickname) next.nicknamesByUserId[tid] = res.data.nickname;
        else delete next.nicknamesByUserId[tid];
        return next;
      });
    },
    [apiUrl, selectedGroupId]
  );

  const refreshMedia = useCallback(async () => {
    try {
      if (isGroupChat && selectedGroupId) {
        const mRes = await axios.get(
          apiUrl(`/api/conversation-settings/group/${selectedGroupId}/media`),
          { withCredentials: true }
        );
        setMediaItems(mRes.data?.items || []);
      } else if (!isGroupChat && selectedFriendId) {
        const mRes = await axios.get(
          apiUrl(`/api/conversation-settings/dm/${selectedFriendId}/media`),
          { withCredentials: true }
        );
        setMediaItems(mRes.data?.items || []);
      }
    } catch (e) {
      console.error(e);
    }
  }, [apiUrl, isGroupChat, selectedFriendId, selectedGroupId]);

  return {
    settings,
    loading,
    error,
    mediaItems,
    themeKey,
    threadClass,
    bubbleClasses,
    displayTitleDm,
    resolveGroupMemberLabel,
    updateTheme,
    saveDmNickname,
    saveGroupNickname,
    refreshMedia,
  };
}
